/**
 * SHOW THE WORK — the tool timeline, visible WHILE the turn runs.
 *
 * ══ WHY A SPINNER WOULD NOT DO ═════════════════════════════════════════════
 *
 * This estate has been burned twice by a process that reported the ACT instead
 * of the OUTCOME: a match generator that logged "Match generation completed"
 * while persisting nothing for 264 days, and an uploader that reported
 * "submitted" as "accepted". A spinner is the UI version of that line — it says
 * something is happening and nothing about WHAT. So the phases are named, the
 * tool being run is named, and the completed tool-call count is shown as it
 * rises.
 *
 * The phase vocabulary is the worker's own (`TurnPhase`), relayed through the
 * STATUS item that every poll reads. Nothing here is inferred from elapsed time.
 */
import { Check, Database, Loader2, PenLine, Search, Shield } from 'lucide-react';

import type { TurnPhase } from '../protocol';

const LADDER: { phase: TurnPhase; label: string; icon: typeof Shield }[] = [
  { phase: 'gated', label: 'Screening the question', icon: Shield },
  // True for a catalogue answer and for a row-level one (#3535: "Choosing a metric" / "Reading the
  // catalogue" were shown while the agent ran SQL over member records).
  { phase: 'thinking', label: 'Choosing how to answer', icon: Search },
  { phase: 'tool', label: 'Reading the data', icon: Database },
  { phase: 'writing', label: 'Writing the answer', icon: PenLine },
];

function positionOf(phase: TurnPhase): number {
  const index = LADDER.findIndex((step) => step.phase === phase);
  if (index >= 0) return index;
  return phase === 'done' ? LADDER.length : 0;
}

export function ToolTimeline({
  phase,
  tool,
  toolCallsDone,
  iteration,
  elapsedMs,
}: {
  phase: TurnPhase;
  tool: string | null;
  toolCallsDone: number;
  iteration: number;
  elapsedMs: number;
}) {
  const current = positionOf(phase);
  return (
    <ol className="space-y-1.5" aria-label="Turn progress">
      {LADDER.map((step, index) => {
        const done = index < current;
        const active = index === current;
        const Icon = step.icon;
        return (
          <li
            key={step.phase}
            /* A PENDING step is recessed by WEIGHT, never by opacity:
               `text-muted-foreground/60` measured 2.30:1 in light mode, and no
               opacity below /100 reaches AA's 4.5:1. See the note in
               StateViews.tsx for the arithmetic. */
            className={`flex items-center gap-2 text-sm ${
              active ? 'font-medium text-foreground' : 'text-muted-foreground'
            }`}
          >
            {done ? (
              <Check className="h-3.5 w-3.5 shrink-0" aria-hidden />
            ) : active ? (
              <Loader2 className="h-3.5 w-3.5 shrink-0 motion-safe:animate-spin" aria-hidden />
            ) : (
              <Icon className="h-3.5 w-3.5 shrink-0" aria-hidden />
            )}
            <span>{step.label}</span>
            {active && step.phase === 'tool' && tool && (
              <span className="rounded bg-muted px-1.5 py-0.5 font-mono text-[11px]">{tool}</span>
            )}
          </li>
        );
      })}
      <li className="pt-1 text-[11px] text-muted-foreground">
        {toolCallsDone} tool call{toolCallsDone === 1 ? '' : 's'} completed · iteration{' '}
        {Math.max(1, iteration)} · {Math.round(elapsedMs / 1000)} s elapsed
      </li>
    </ol>
  );
}

/** The tools a FINISHED turn actually ran, in order. */
export function ToolTrail({ tools }: { tools: string[] }) {
  if (tools.length === 0) return null;
  return (
    <div className="flex flex-wrap items-center gap-1.5 text-[11px] text-muted-foreground">
      <span className="uppercase tracking-wide">Tools</span>
      {tools.map((tool, index) => (
        <span key={`${tool}-${index}`} className="rounded bg-muted px-1.5 py-0.5 font-mono">
          {tool}
        </span>
      ))}
    </div>
  );
}
